<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
      </template>
      <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ $t("STOCK.PRODUCT_MOVEMENTS.TITLE") }}</v-toolbar-title>
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text class="pt-4 pb-0">
              <StockProductMovementFilters ref="productFilters" @apply="onProductFiltersApply" />
            </v-card-text>
            <v-card-text v-if="summaryTotals.length" class="pb-0 pt-3">
              <div class="caption grey--text text--darken-1 mb-1">
                {{ $t("STOCK.MOVEMENTS.SUMMARY") }}
              </div>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="total in summaryTotals"
                  :key="`${total.organization_id}-${total.currency_code}`"
                  small
                  class="mr-2 mb-2"
                  color="grey lighten-3"
                >
                  <span class="font-weight-medium mr-1">{{ total.organization_code }}:</span>
                  {{ formatSignedMoney(total.signed_inventory_value_net, total.currency_code) }}
                </v-chip>
              </div>
            </v-card-text>
            <v-card-text class="pa-0">
              <Table
                v-if="userStatePrepared"
                ref="stock-product-movements-table"
                height="500"
                name="stock-product-movements-table"
                :headers="headers"
                :server-data-options="{ method: tableItemsMethod }"
                :filters-values="initialTableFilters"
                :persistent-filters="false"
                :items-per-page="25"
                :loading="isProcessing"
                :filters="tableFilters"
                dense
                fixed-header
                filterable
              >
                <template
                  v-for="(header, index) in headers"
                  v-slot:[`item.${header.value}`]="{ item }"
                >
                  <template v-if="header.value === 'movement_direction'">
                    <v-chip
                      :key="index"
                      small
                      :color="directionColor(item.item)"
                      dark
                      class="font-weight-bold"
                    >
                      <v-icon left small>{{ directionIcon(item.item) }}</v-icon>
                      {{ directionSign(item.item) }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'action_code'">
                    <router-link
                      v-if="item.item.action_id"
                      :key="index"
                      :to="movementViewRoute(item.item)"
                    >
                      {{ item.item.action_code }}
                    </router-link>
                    <span v-else :key="index">-</span>
                  </template>
                  <template v-else-if="header.value === 'action_type.name'">
                    <div :key="index" class="text-center">
                      <div>{{ actionTypeName(item.item) }}</div>
                      <div
                        v-if="item.item.action_type && item.item.action_type.category"
                        class="caption grey--text text--darken-1"
                      >
                        {{ actionCategoryLabel(item.item.action_type.category) }}
                      </div>
                    </div>
                  </template>
                  <template v-else-if="header.value === 'stock_product.display_name'">
                    {{ productLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'lot_number'">
                    <v-tooltip v-if="item.item.lot_number" :key="index" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          text
                          x-small
                          color="primary"
                          class="text-none px-1"
                          v-bind="attrs"
                          v-on="on"
                          @click="traceLot(item.item)"
                        >
                          {{ item.item.lot_number }}
                        </v-btn>
                      </template>
                      <span>{{ $t("STOCK.PRODUCT_MOVEMENTS.TRACE_LOT") }}</span>
                    </v-tooltip>
                    <span v-else :key="`${index}-no-lot`">-</span>
                  </template>
                  <template v-else-if="header.value === 'signed_quantity'">
                    {{ formatQuantity(item.item.signed_quantity, item.item.unit) }}
                  </template>
                  <template v-else-if="header.value === 'signed_line_total_net'">
                    {{ formatSignedMoney(
                      lineCommercialNet(item.item),
                      item.item.currency_code
                    ) }}
                  </template>
                  <template v-else-if="header.value === 'inventory_cost_net'">
                    {{ formatFinancialValue(
                      item.item.inventory_cost_net,
                      item.item.currency_code
                    ) }}
                  </template>
                  <template v-else-if="header.value === 'cost_of_goods_net'">
                    {{ formatFinancialValue(
                      lineCostOfGoodsNet(item.item),
                      item.item.currency_code
                    ) }}
                  </template>
                  <template v-else-if="header.value === 'margin_net'">
                    <span
                      v-if="isOutboundMovement(item.item)"
                      :class="marginTextClass(item.item.margin_net)"
                    >
                      {{ formatFinancialValue(item.item.margin_net, item.item.currency_code) }}
                    </span>
                    <span v-else :key="`${index}-line-margin`">-</span>
                  </template>
                  <template v-else-if="header.value === 'margin_pct'">
                    <span
                      v-if="isOutboundMovement(item.item)"
                      :class="marginTextClass(item.item.margin_net)"
                    >
                      {{ lineMovementMarginPct(item.item) }}
                    </span>
                    <span v-else :key="`${index}-line-margin-pct`">-</span>
                  </template>
                  <template v-else-if="header.value === 'deal.label'">
                    {{ dealLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'warehouse.name'">
                    {{ warehouseLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'counterparty.label'">
                    {{ counterpartyLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'purchase_order'">
                    <router-link
                      v-if="item.item.purchase_order"
                      :key="index"
                      :to="{
                        name: 'stock-purchase-orders-view',
                        params: { id: item.item.purchase_order.id },
                      }"
                    >
                      {{ item.item.purchase_order.code }}
                    </router-link>
                    <span v-else :key="index">-</span>
                  </template>
                  <template v-else-if="header.value === 'sales_order'">
                    <router-link
                      v-if="item.item.sales_order"
                      :key="index"
                      :to="{
                        name: 'stock-sales-orders-view',
                        params: { id: item.item.sales_order.id },
                      }"
                    >
                      {{ item.item.sales_order.code }}
                    </router-link>
                    <span v-else :key="index">-</span>
                  </template>
                  <template v-else>
                    {{ item.value || "-" }}
                  </template>
                </template>
              </Table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import StockProductMovementFilters from "@/views/partials/stock/StockProductMovementFilters.vue";
import stockMovementRowDisplayMixin from "@/views/partials/stock/stockMovementRowDisplay.mixin";
import { formatStockQuantity } from "@/common/stock/stockNumberFormat";
import { GET_STOCK_PRODUCT_MOVEMENTS } from "@/store/stock/stockProductMovements.module";
import {
  GET_STOCK_ACTION_TYPE_OPTIONS,
} from "@/store/stock/stockReceipts.module";

const PRODUCT_CATALOG_FILTER_KEYS = [
  "stock_product_search",
  "stock_product_department_id",
  "stock_product_category_id",
  "temperature_id",
  "packaging_id",
  "stock_tag_ids",
  "meat_mapping_value_id",
  "description_mapping_value_id",
  "stock_product_id",
  "stock_lot_id",
  "lot_number",
];

function formatIsoDate(date) {
  return date.toISOString().slice(0, 10);
}

function defaultDateRange() {
  const dateTo = new Date();
  const dateFrom = new Date();
  dateFrom.setMonth(dateFrom.getMonth() - 3);
  return {
    date_from: formatIsoDate(dateFrom),
    date_to: formatIsoDate(dateTo),
  };
}

export default {
  name: "StockProductMovements",
  components: { Table, StockProductMovementFilters },
  mixins: [stockMovementRowDisplayMixin],
  data() {
    const defaultDates = defaultDateRange();

    return {
      tab: "tab-1",
      isProcessing: false,
      userStatePrepared: false,
      tableItemsMethod: GET_STOCK_PRODUCT_MOVEMENTS,
      tableFilters: [],
      productCatalogFilters: {},
      defaultFilterValues: defaultDates,
      headers: [
        {
          text: this.$t("STOCK.RECEIPTS.DIRECTION"),
          value: "movement_direction",
          sortable: false,
          align: "center",
          width: "5%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.DATE"),
          value: "action_date",
          sortable: true,
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.DOCUMENT_CODE"),
          value: "action_code",
          sortable: false,
          align: "center",
          width: "9%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.MOVEMENT_TYPE"),
          value: "action_type.name",
          sortable: false,
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("STOCK.PRODUCTS.TITLE"),
          value: "stock_product.display_name",
          sortable: false,
          align: "center",
          width: "12%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.LOT_NUMBER"),
          value: "lot_number",
          sortable: false,
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("STOCK.PRODUCT_MOVEMENTS.SIGNED_QTY"),
          value: "signed_quantity",
          sortable: false,
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("STOCK.MOVEMENTS.TOTAL_NET"),
          value: "signed_line_total_net",
          sortable: false,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.MOVEMENTS.INVENTORY_COST"),
          value: "inventory_cost_net",
          sortable: false,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.MOVEMENTS.TOTAL_COGS"),
          value: "cost_of_goods_net",
          sortable: false,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.MOVEMENTS.TOTAL_MARGIN"),
          value: "margin_net",
          sortable: false,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.MOVEMENTS.MARGIN_PCT"),
          value: "margin_pct",
          sortable: false,
          align: "center",
          width: "5%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.PURCHASE_ORDER"),
          value: "purchase_order",
          sortable: false,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.MOVEMENTS.SALES_ORDER"),
          value: "sales_order",
          sortable: false,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.PURCHASE_ORDERS.DEAL"),
          value: "deal.label",
          sortable: false,
          align: "center",
          width: "9%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.COUNTERPARTY"),
          value: "counterparty.label",
          sortable: false,
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("STOCK.RECEIPTS.WAREHOUSE"),
          value: "warehouse.name",
          sortable: false,
          align: "center",
          width: "10%",
        },
      ],
    };
  },
  computed: {
    stockContextVersion() {
      return this.$store.getters.stockContextVersion;
    },
    summaryTotals() {
      return this.$store.getters.stockProductMovementsSummary?.totals_by_organization || [];
    },
    initialTableFilters() {
      return { ...this.defaultFilterValues };
    },
  },
  watch: {
    stockContextVersion() {
      this.loadTableFilters().finally(() => this.reloadTable());
    },
  },
  mounted() {
    this.loadTableFilters().finally(() => {
      this.userStatePrepared = true;
      this.$nextTick(() => this.applyTableFilters());
    });
  },
  methods: {
    movementViewRoute(row) {
      const category = row.action_category || (row.action_type && row.action_type.category);
      if (category === "sale") {
        return { name: "stock-sales-view", params: { id: row.action_id } };
      }
      if (category === "write_off") {
        return { name: "stock-write-offs-view", params: { id: row.action_id } };
      }
      if (category === "transfer") {
        return { name: "stock-transfers-view", params: { id: row.action_id } };
      }
      return { name: "stock-receipts-view", params: { id: row.action_id } };
    },
    productLabel(row) {
      const product = row.stock_product;
      if (!product) {
        return "-";
      }
      return product.display_name || product.code || "-";
    },
    warehouseLabel(row) {
      const wh = row.warehouse;
      if (!wh) {
        return "-";
      }
      if (wh.code && wh.name) {
        return `${wh.code} — ${wh.name}`;
      }
      return wh.name || wh.code || "-";
    },
    counterpartyLabel(row) {
      const counterparty = row.counterparty;
      if (!counterparty) {
        return "-";
      }
      return counterparty.label || "-";
    },
    formatSignedMoney(value, currency) {
      const amount = Number(value || 0);
      const prefix = amount > 0 ? "+" : "";
      return `${prefix}${amount.toFixed(2)} ${currency || ""}`.trim();
    },
    lineCommercialNet(row) {
      if (row.signed_commercial_line_net != null) {
        return row.signed_commercial_line_net;
      }
      const allocated = Number(row.allocated_extra_cost || 0);
      const lineNet = Number(row.line_total_net || 0);
      const total = lineNet + allocated;
      return this.directionValue(row) < 0 ? -total : total;
    },
    lineCostOfGoodsNet(row) {
      if (row.cost_of_goods_net != null) {
        return row.cost_of_goods_net;
      }
      if (this.isInboundMovement(row)) {
        return row.inventory_cost_net;
      }
      return null;
    },
    lineMovementMarginPct(row) {
      if (!this.isOutboundMovement(row)) {
        return "-";
      }
      if (row.margin_pct != null) {
        return `${Number(row.margin_pct).toFixed(2)}%`;
      }
      if (row.margin_net != null && row.line_total_net) {
        const pct = (Number(row.margin_net) / Number(row.line_total_net)) * 100;
        return `${pct.toFixed(2)}%`;
      }
      return "-";
    },
    formatQuantity(value, unit) {
      const qty = Number(value || 0);
      const prefix = qty > 0 ? "+" : "";
      const unitCode = unit && unit.code ? ` ${unit.code}` : "";
      return `${prefix}${formatStockQuantity(qty)}${unitCode}`.trim();
    },
    onProductFiltersApply(filters, options = {}) {
      this.productCatalogFilters = filters || {};
      this.applyTableFilters(options);
    },
    traceLot(row) {
      const filters = this.$refs.productFilters;
      if (!filters) {
        return;
      }

      filters.applyTrace({
        stock_product_id: row.stock_product_id || (row.stock_product && row.stock_product.id) || null,
        lot_number: row.lot_number || "",
        product: row.stock_product || null,
      });
    },
    loadTableFilters() {
      const directionItems = [
        { id: 1, label: this.$t("STOCK.RECEIPTS.DIRECTION_IN") },
        { id: -1, label: this.$t("STOCK.RECEIPTS.DIRECTION_OUT") },
        { id: 0, label: this.$t("STOCK.RECEIPTS.DIRECTION_NEUTRAL") },
      ];

      const categoryItems = [
        "receipt",
        "sale",
        "transfer",
        "write_off",
        "production",
        "adjustment",
      ].map((category) => ({
        id: category,
        label: this.actionCategoryLabel(category),
      }));

      return this.$store
        .dispatch(GET_STOCK_ACTION_TYPE_OPTIONS)
        .then((actionTypes) => {
          const actionTypeItems = (actionTypes || []).map((type) => ({
            id: type.id,
            label: type.label || type.name,
          }));

          this.tableFilters = [
            {
              key: "direction",
              label: this.$t("STOCK.RECEIPTS.DIRECTION"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: directionItems,
            },
            {
              key: "action_type_id",
              label: this.$t("STOCK.RECEIPTS.MOVEMENT_TYPE"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: actionTypeItems,
            },
            {
              key: "action_category",
              label: this.$t("STOCK.RECEIPTS.ACTION_CATEGORY"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: categoryItems,
            },
            {
              key: "date_from",
              label: `${this.$t("STOCK.RECEIPTS.DATE")} (${this.$t("GENERAL.LABELS.START_DATE")})`,
              type: "date",
            },
            {
              key: "date_to",
              label: `${this.$t("STOCK.RECEIPTS.DATE")} (${this.$t("GENERAL.LABELS.END_DATE")})`,
              type: "date",
            },
          ];
        })
        .catch(() => {
          this.tableFilters = [];
        });
    },
    applyTableFilters(options = {}) {
      const table = this.$refs["stock-product-movements-table"];
      if (!table) {
        return;
      }

      const filters = {
        ...this.defaultFilterValues,
        ...table.serverFilters,
      };

      PRODUCT_CATALOG_FILTER_KEYS.forEach((key) => {
        delete filters[key];
      });
      Object.assign(filters, this.productCatalogFilters);

      if (options.clearDates) {
        delete filters.date_from;
        delete filters.date_to;
      }

      table.serverFilters = filters;
      table.fetchServerData(true);
    },
    reloadTable() {
      this.$nextTick(() => this.applyTableFilters());
    },
  },
};
</script>
